import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  listManufacturers, addManufacturer, deleteManufacturer, updateManufacturer,
  discoverCatalog, scrapeCatalog, importCatalogCsv, importCatalogPdf, importCatalogLdt,
  updateCatalog, getManufacturerProducts, getManufacturerHistory, importLocalCatalogs
} from '../lib/api';
import {
  Package, Plus, Search, RefreshCw, Upload, Globe, Trash2,
  ChevronDown, ChevronUp, Loader2, CheckCircle, AlertTriangle,
  Clock, Database, Zap, FileSpreadsheet, Eye, Edit3, Save, X,
  ExternalLink, Mail, Phone, Percent, Star
} from 'lucide-react';

const t = (lang, pl, en) => lang === 'pl' ? pl : en;

const statusBadge = (status) => ({
  success: { cls: 'bg-green-500/20 text-green-400', icon: CheckCircle, label: 'Zsynchronizowany' },
  syncing: { cls: 'bg-blue-500/20 text-blue-400', icon: Loader2, label: 'Synchronizacja...' },
  error: { cls: 'bg-red-500/20 text-red-400', icon: AlertTriangle, label: 'Błąd' },
  never: { cls: 'bg-muted text-muted-foreground', icon: Clock, label: 'Nigdy' },
}[status] || { cls: 'bg-muted text-muted-foreground', icon: Clock, label: status });

export default function Catalogs({ lang, tenantId }) {
  const [manufacturers, setManufacturers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newMfr, setNewMfr] = useState({ name: '', website: '', method: 'scraper' });
  const [selected, setSelected] = useState(null);
  const [products, setProducts] = useState([]);
  const [history, setHistory] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [editMfr, setEditMfr] = useState(null);
  const [editForm, setEditForm] = useState({});
  const fileRef = useRef(null);
  const pdfRef = useRef(null);
  const ldtRef = useRef(null);

  useEffect(() => { load(); }, [tenantId]);

  async function load() {
    setLoading(true);
    try {
      const data = await listManufacturers(tenantId);
      setManufacturers(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  async function handleAdd() {
    if (!newMfr.name) return;
    try {
      await addManufacturer(newMfr, tenantId);
      toast.success(t(lang, `Dodano ${newMfr.name}`, `Added ${newMfr.name}`));
      setShowAdd(false);
      setNewMfr({ name: '', website: '', method: 'scraper' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  }

  async function handleDelete(mfr) {
    if (!window.confirm(t(lang, `Usunąć ${mfr.name}?`, `Delete ${mfr.name}?`))) return;
    try {
      await deleteManufacturer(mfr.id, tenantId);
      toast.success(t(lang, 'Usunięto', 'Deleted'));
      setSelected(null);
      load();
    } catch (e) { toast.error(e.message); }
  }

  function startEdit(mfr) {
    setEditMfr(mfr);
    setEditForm({
      name: mfr.name || '',
      website: mfr.website || '',
      catalog_url: mfr.catalog_url || '',
      products_url: mfr.products_url || '',
      downloads_url: mfr.downloads_url || '',
      archiproducts_url: mfr.archiproducts_url || '',
      contact_email: mfr.contact_email || '',
      contact_phone: mfr.contact_phone || '',
      payment_terms: mfr.payment_terms || '',
      discount_pct: mfr.discount_pct || '',
      notes: mfr.notes || '',
      is_preferred: mfr.is_preferred || false,
    });
  }

  async function handleSaveEdit() {
    if (!editMfr) return;
    try {
      await updateManufacturer(editMfr.id, editForm, tenantId);
      toast.success(t(lang, `Zapisano zmiany dla ${editForm.name}`, `Saved changes for ${editForm.name}`));
      setEditMfr(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  }

  async function handleDiscover(mfr) {
    setActionLoading(`discover-${mfr.id}`);
    try {
      const result = await discoverCatalog(mfr.id, tenantId);
      if (result.catalog_url) {
        toast.success(t(lang, `Znaleziono katalog: ${result.catalog_url}`, `Found catalog: ${result.catalog_url}`));
      } else {
        toast.info(result.notes || t(lang, 'Nie znaleziono katalogu', 'Catalog not found'));
      }
      load();
    } catch (e) { toast.error(e.message); }
    setActionLoading(null);
  }

  async function handleScrape(mfr) {
    setActionLoading(`scrape-${mfr.id}`);
    toast.info(t(lang, 'Scrapowanie katalogu... to może chwilę potrwać', 'Scraping catalog... this may take a moment'));
    try {
      const result = await scrapeCatalog(mfr.id, tenantId);
      const imp = result.import || {};
      toast.success(t(lang,
        `Gotowe! +${imp.added} nowych, ~${imp.updated} zaktualizowanych (${result.total_products} znalezionych)`,
        `Done! +${imp.added} new, ~${imp.updated} updated (${result.total_products} found)`
      ));
      load();
      if (selected?.id === mfr.id) selectMfr(mfr);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setActionLoading(null);
  }

  async function handleFileUpload(mfr, file, type) {
    if (!file) return;
    setActionLoading(`${type}-${mfr.id}`);
    try {
      let result;
      if (type === 'csv') {
        result = await importCatalogCsv(mfr.id, file, tenantId);
      } else if (type === 'pdf') {
        result = await importCatalogPdf(mfr.id, file, tenantId);
      } else if (type === 'ldt') {
        result = await importCatalogLdt(mfr.id, file, tenantId);
      }
      const imp = result.import || result || {};
      toast.success(t(lang,
        `Import ${type.toUpperCase()}: +${imp.added || 0} nowych, ~${imp.updated || 0} zaktualizowanych (${result.products_found || result.products_parsed || 0} znalezionych)`,
        `${type.toUpperCase()} Import: +${imp.added || 0} new, ~${imp.updated || 0} updated (${result.products_found || result.products_parsed || 0} found)`
      ));
      load();
      if (selected?.id === mfr.id) selectMfr(mfr);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setActionLoading(null);
  }

  async function handleUpdate(mfr) {
    setActionLoading(`update-${mfr.id}`);
    toast.info(t(lang, 'Aktualizacja katalogu...', 'Updating catalog...'));
    try {
      const result = await updateCatalog(mfr.id, tenantId);
      const imp = result.import || {};
      toast.success(t(lang,
        `Zaktualizowano: +${imp.added} nowych, ~${imp.updated} zmian`,
        `Updated: +${imp.added} new, ~${imp.updated} changes`
      ));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setActionLoading(null);
  }

  async function handleBulkImport() {
    setActionLoading('bulk');
    toast.info(t(lang, 'Importowanie 35 katalogów CSV...', 'Importing 35 CSV catalogs...'));
    try {
      const result = await importLocalCatalogs(tenantId);
      toast.success(t(lang,
        `Zaimportowano ${result.brands_imported} marek`,
        `Imported ${result.brands_imported} brands`
      ));
      load();
    } catch (e) { toast.error(e.message); }
    setActionLoading(null);
  }

  async function selectMfr(mfr) {
    setSelected(mfr);
    setProductSearch('');
    try {
      const [prods, hist] = await Promise.all([
        getManufacturerProducts(mfr.id, tenantId),
        getManufacturerHistory(mfr.id, tenantId),
      ]);
      setProducts(prods.products || []);
      setHistory(hist.history || []);
    } catch (e) {
      setProducts([]);
      setHistory([]);
    }
  }

  async function searchProducts(mfr, query) {
    setProductSearch(query);
    try {
      const prods = await getManufacturerProducts(mfr.id, tenantId, query);
      setProducts(prods.products || []);
    } catch (e) { /* ignore */ }
  }

  const isLoading = (key) => actionLoading === key;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Package className="w-6 h-6 text-primary" />
            {t(lang, 'Katalogi Producentów', 'Manufacturer Catalogs')}
          </h1>
          <p className="text-muted-foreground text-sm">
            {t(lang, 'Dodaj producenta, zeskanuj katalog lub importuj CSV', 'Add manufacturer, scan catalog or import CSV')}
          </p>
        </div>
        <div className="flex gap-2">
          {manufacturers.length === 0 && (
            <Button variant="outline" onClick={handleBulkImport} disabled={!!actionLoading} className="gap-1.5">
              {isLoading('bulk') ? <Loader2 className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />}
              {t(lang, 'Importuj 35 katalogów', 'Import 35 catalogs')}
            </Button>
          )}
          <Dialog open={showAdd} onOpenChange={setShowAdd}>
            <DialogTrigger asChild>
              <Button className="gap-1.5"><Plus className="w-4 h-4" />{t(lang, 'Dodaj Producenta', 'Add Manufacturer')}</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t(lang, 'Nowy producent', 'New Manufacturer')}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <Input placeholder={t(lang, 'Nazwa producenta (np. Philips)', 'Manufacturer name')}
                  value={newMfr.name} onChange={e => setNewMfr({ ...newMfr, name: e.target.value })} />
                <Input placeholder="https://www.manufacturer.com"
                  value={newMfr.website} onChange={e => setNewMfr({ ...newMfr, website: e.target.value })} />
                <div className="flex gap-2">
                  {['scraper', 'csv', 'api'].map(m => (
                    <Button key={m} size="sm"
                      variant={newMfr.method === m ? 'default' : 'outline'}
                      onClick={() => setNewMfr({ ...newMfr, method: m })}>
                      {m === 'scraper' ? '🤖 Auto' : m === 'csv' ? '📄 CSV' : '🔌 API'}
                    </Button>
                  ))}
                </div>
                <Button onClick={handleAdd} className="w-full" disabled={!newMfr.name}>{t(lang, 'Dodaj', 'Add')}</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {/* LEFT: Manufacturer list */}
        <div className="space-y-2">
          {manufacturers.map(mfr => {
            const st = statusBadge(mfr.last_sync_status);
            const isSelected = selected?.id === mfr.id;
            return (
              <Card key={mfr.id}
                className={`cursor-pointer transition-all ${isSelected ? 'ring-2 ring-primary' : 'hover:border-primary/30'}`}
                onClick={() => selectMfr(mfr)}>
                <CardContent className="pt-4 pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold">{mfr.name}</div>
                      <div className="text-xs text-muted-foreground">{mfr.products_count} {t(lang, 'produktów', 'products')}</div>
                    </div>
                    <Badge className={`text-xs ${st.cls}`}>{st.label}</Badge>
                  </div>
                  {mfr.last_sync && (
                    <div className="text-xs text-muted-foreground mt-1">
                      {t(lang, 'Sync', 'Sync')}: {new Date(mfr.last_sync).toLocaleDateString('pl')}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
          {manufacturers.length === 0 && !loading && (
            <Card>
              <CardContent className="pt-8 pb-8 text-center text-muted-foreground">
                <Package className="w-8 h-8 mx-auto mb-2 opacity-30" />
                {t(lang, 'Brak producentów. Dodaj pierwszego lub importuj katalogi CSV.', 'No manufacturers. Add first or import CSV catalogs.')}
              </CardContent>
            </Card>
          )}
        </div>

        {/* RIGHT: Manufacturer detail */}
        <div className="md:col-span-2 space-y-4">
          {selected ? (
            <>
              {/* Header */}
              <Card>
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h2 className="text-xl font-bold">{selected.name}</h2>
                      {selected.website && <div className="text-sm text-muted-foreground">{selected.website}</div>}
                      {selected.catalog_url && <div className="text-xs text-primary">{t(lang, 'Katalog', 'Catalog')}: {selected.catalog_url}</div>}
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => startEdit(selected)}><Edit3 className="w-4 h-4 text-blue-400" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(selected)}><Trash2 className="w-4 h-4 text-red-400" /></Button>
                    </div>
                  </div>

                  {/* Links with icons */}
                  <div className="flex flex-wrap gap-2 mb-3 text-xs">
                    {selected.website && (
                      <a href={selected.website} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-muted-foreground hover:text-primary">
                        <Globe className="w-3 h-3" /> Strona
                      </a>
                    )}
                    {selected.catalog_url && (
                      <a href={selected.catalog_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-muted-foreground hover:text-primary">
                        <ExternalLink className="w-3 h-3" /> Katalog
                      </a>
                    )}
                    {selected.products_url && (
                      <a href={selected.products_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-muted-foreground hover:text-primary">
                        <Package className="w-3 h-3" /> Produkty
                      </a>
                    )}
                    {selected.downloads_url && (
                      <a href={selected.downloads_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-muted-foreground hover:text-primary">
                        <Database className="w-3 h-3" /> Pliki LDT
                      </a>
                    )}
                    {selected.archiproducts_url && (
                      <a href={selected.archiproducts_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-muted-foreground hover:text-primary">
                        <Star className="w-3 h-3" /> ArchiProducts
                      </a>
                    )}
                  </div>
                  {selected.notes && <div className="text-xs text-amber-400 mb-3">⚠️ {selected.notes}</div>}

                  {/* Action buttons */}
                  <div className="flex flex-wrap gap-2">
                    {selected.website && (
                      <Button size="sm" variant="outline" onClick={() => handleDiscover(selected)} disabled={!!actionLoading} className="gap-1">
                        {isLoading(`discover-${selected.id}`) ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                        {t(lang, 'Odkryj katalog', 'Discover')}
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => handleScrape(selected)} disabled={!!actionLoading} className="gap-1">
                      {isLoading(`scrape-${selected.id}`) ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
                      {t(lang, 'Skanuj AI', 'AI Scan')}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={!!actionLoading} className="gap-1">
                      {isLoading(`csv-${selected.id}`) ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                      CSV
                    </Button>
                    <input ref={fileRef} type="file" accept=".csv" className="hidden"
                      onChange={e => { if (e.target.files[0]) handleFileUpload(selected, e.target.files[0], 'csv'); e.target.value = ''; }} />
                    <Button size="sm" variant="outline" onClick={() => pdfRef.current?.click()} disabled={!!actionLoading} className="gap-1 text-orange-400">
                      {isLoading(`pdf-${selected.id}`) ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                      PDF
                    </Button>
                    <input ref={pdfRef} type="file" accept=".pdf" className="hidden"
                      onChange={e => { if (e.target.files[0]) handleFileUpload(selected, e.target.files[0], 'pdf'); e.target.value = ''; }} />
                    <Button size="sm" variant="outline" onClick={() => ldtRef.current?.click()} disabled={!!actionLoading} className="gap-1 text-green-400">
                      {isLoading(`ldt-${selected.id}`) ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                      LDT/ZIP
                    </Button>
                    <input ref={ldtRef} type="file" accept=".ldt,.zip" className="hidden"
                      onChange={e => { if (e.target.files[0]) handleFileUpload(selected, e.target.files[0], 'ldt'); e.target.value = ''; }} />
                    <Button size="sm" onClick={() => handleUpdate(selected)} disabled={!!actionLoading} className="gap-1">
                      {isLoading(`update-${selected.id}`) ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                      {t(lang, 'Aktualizuj', 'Update')}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Products */}
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">{t(lang, 'Produkty', 'Products')} ({products.length})</CardTitle>
                    <Input placeholder={t(lang, 'Szukaj...', 'Search...')} className="w-48 h-8 text-sm"
                      value={productSearch} onChange={e => searchProducts(selected, e.target.value)} />
                  </div>
                </CardHeader>
                <CardContent>
                  {products.length > 0 ? (
                    <div className="max-h-96 overflow-y-auto space-y-1.5">
                      {products.map((p, i) => (
                        <div key={p.id || i} className="flex items-center justify-between px-3 py-2 border rounded text-sm hover:bg-muted/30">
                          <div className="flex-1">
                            <span className="font-medium">{p.name}</span>
                            {p.sku && <span className="text-xs text-muted-foreground ml-2">{p.sku}</span>}
                          </div>
                          <div className="flex items-center gap-2">
                            {p.base_price > 0 && <span className="font-mono text-xs">{p.base_price} {p.currency || 'PLN'}</span>}
                            <Badge variant="outline" className="text-xs">{p.category}</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center text-muted-foreground py-8 text-sm">
                      {t(lang, 'Brak produktów. Zeskanuj katalog lub importuj CSV.', 'No products. Scan catalog or import CSV.')}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* History */}
              {history.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">{t(lang, 'Historia synchronizacji', 'Sync History')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-1.5">
                      {history.slice().reverse().map((h, i) => (
                        <div key={i} className="flex items-center justify-between px-3 py-2 border rounded text-xs">
                          <span>{new Date(h.date).toLocaleString('pl')}</span>
                          <Badge variant="outline">{h.method}</Badge>
                          <span className="text-green-400">+{h.added}</span>
                          <span className="text-yellow-400">~{h.updated}</span>
                          <Badge className={h.status === 'success' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>{h.status}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card>
              <CardContent className="pt-16 pb-16 text-center text-muted-foreground">
                <Eye className="w-10 h-10 mx-auto mb-3 opacity-20" />
                <p>{t(lang, 'Wybierz producenta z listy lub dodaj nowego', 'Select a manufacturer or add new one')}</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
      {/* Edit Manufacturer Dialog */}
      {editMfr && (
        <Dialog open={!!editMfr} onOpenChange={() => setEditMfr(null)}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit3 className="w-5 h-5" />
                {t(lang, 'Edytuj producenta', 'Edit manufacturer')}: {editMfr.name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 mt-2">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Nazwa</label>
                <Input value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 flex items-center gap-1"><Globe className="w-3 h-3" /> Strona WWW</label>
                <Input value={editForm.website} onChange={e => setEditForm({...editForm, website: e.target.value})} placeholder="https://..." />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 flex items-center gap-1"><ExternalLink className="w-3 h-3" /> URL katalogu (PDF / strona z katalogiem)</label>
                <Input value={editForm.catalog_url} onChange={e => setEditForm({...editForm, catalog_url: e.target.value})} placeholder="https://..." />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 flex items-center gap-1"><Package className="w-3 h-3" /> URL produktów (listing)</label>
                <Input value={editForm.products_url} onChange={e => setEditForm({...editForm, products_url: e.target.value})} placeholder="https://..." />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 flex items-center gap-1"><Database className="w-3 h-3" /> URL plików do pobrania (LDT/IES)</label>
                <Input value={editForm.downloads_url} onChange={e => setEditForm({...editForm, downloads_url: e.target.value})} placeholder="https://..." />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 flex items-center gap-1"><Star className="w-3 h-3" /> ArchiProducts URL</label>
                <Input value={editForm.archiproducts_url} onChange={e => setEditForm({...editForm, archiproducts_url: e.target.value})} placeholder="https://www.archiproducts.com/en/..." />
              </div>

              <hr className="border-border" />

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 flex items-center gap-1"><Mail className="w-3 h-3" /> Email kontaktowy</label>
                  <Input value={editForm.contact_email} onChange={e => setEditForm({...editForm, contact_email: e.target.value})} placeholder="biuro@..." />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 flex items-center gap-1"><Phone className="w-3 h-3" /> Telefon</label>
                  <Input value={editForm.contact_phone} onChange={e => setEditForm({...editForm, contact_phone: e.target.value})} placeholder="+48..." />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground mb-1">Warunki płatności</label>
                  <Input value={editForm.payment_terms} onChange={e => setEditForm({...editForm, payment_terms: e.target.value})} placeholder="np. 30 dni, przedpłata" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 flex items-center gap-1"><Percent className="w-3 h-3" /> Rabat %</label>
                  <Input type="number" value={editForm.discount_pct} onChange={e => setEditForm({...editForm, discount_pct: parseFloat(e.target.value) || 0})} placeholder="0" />
                </div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1">Notatki</label>
                <textarea className="w-full bg-background border border-border rounded-md p-2 text-sm min-h-[60px]"
                  value={editForm.notes} onChange={e => setEditForm({...editForm, notes: e.target.value})} placeholder="Uwagi, kontakty, warunki..." />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={editForm.is_preferred} onChange={e => setEditForm({...editForm, is_preferred: e.target.checked})} id="preferred" />
                <label htmlFor="preferred" className="text-sm flex items-center gap-1"><Star className="w-3.5 h-3.5 text-yellow-400" /> Preferowany dostawca</label>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={() => setEditMfr(null)} className="gap-1"><X className="w-4 h-4" /> Anuluj</Button>
                <Button onClick={handleSaveEdit} className="gap-1"><Save className="w-4 h-4" /> Zapisz</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
